import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { eFertilizerFormulationTypes, eNotificationTypes } from '../../../../interfaces/constants';
import {
	IFertilizer,
	IRanchCreateFertilizerForm,
	IRanchFertilizerFormResult,
	RanchFertilizerViewModel
} from '../../modals/fertilizer/interfaces';
import { FertilizerModelService } from '../../modals/fertilizer/service';
import { NotificationService } from '../../../../services/notification.service';

@Component({
	moduleId: module.id,
	selector: 'ranch-fertilizer-form',
	templateUrl: 'main.html',
	styleUrls: ['main.scss']
})

export class RanchFertilizerFormComponent implements OnInit, OnChanges {
	@Input()
	public isRanchAgnostic = false;

	@Input()
	public selectedFertilizerIds: number[] = [];

	@Output()
	public form: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onSubmit: EventEmitter<IRanchFertilizerFormResult> = new EventEmitter<IRanchFertilizerFormResult>();

	public addStates: { value: boolean, display: string }[] = [
		{ value: false, display: 'Choose from List' },
		{ value: true, display: 'Create a custom fertilizer' }
	];
	public fertilizerTabIndex = 0;
	public isCustomFertilizer = false;
	public fertilizer: RanchFertilizerViewModel;
	public fertilizers: IFertilizer[];
	public isFormValid = true;
	public isTouched = false;
	public selectableFertilizers: IFertilizer[] = [];
	private _nitrogenNutrientId = 1;

	constructor(
		private _notificationService: NotificationService,
		private _service: FertilizerModelService
	) { }

	public get areFertilizersSelected(): boolean {
		return this.getSelectedFertilizers().length > 0;
	}

	public get areThereFertilizers(): boolean {
		return !this.selectableFertilizers || this.selectableFertilizers.length === 0;
	}

	public get isOrganicTab(): boolean {
		return this.fertilizerTabIndex === 1;
	}

	ngOnInit(): void {
		this._get();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.selectedFertilizerIds) {
			this.selectableFertilizers = this._getSelectableFertilizers();
		}
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public getSelectedFertilizers(): IFertilizer[] {
		if (!this.fertilizers) {
			return [];
		}

		return this.fertilizers.filter((fertilizer) =>
			fertilizer.checked === true &&
			this.selectedFertilizerIds.indexOf(fertilizer.Id) === -1);
	}

	public getSelectedFertilizerIds(): number[] {
		return this.getSelectedFertilizers().map((fertilizer) => fertilizer.Id);
	}

	public onFertilizerTabChange(tabIndex: number): void {
		this.fertilizerTabIndex = tabIndex;
		this.selectableFertilizers = this._getSelectableFertilizers();
	}

	public submitCustomFertilizer(): void {
		if (!this.fertilizer) {
			return;
		}

		if (this.fertilizer.IsChemical !== false &&
			(!this.fertilizer.Nutrients || this.fertilizer.Nutrients.length === 0)) {

			this._notificationService.generateNotifcation({
				type: eNotificationTypes.ERROR,
				message: `Fertilizer Nutrient information is incomplete. Please try again.`
			});

			return;
		}

		this.onSubmit.emit({
			IsCustomFertilizer: true,
			SelectedFertilizerIds: [],
			Fertilizers: [
				this._buildCustomFertilizerForm()
			],
			CustomFertilizer: this.fertilizer
		});
	}

	public submitSelectedFertilizers(): void {
		this.onSubmit.emit({
			IsCustomFertilizer: false,
			SelectedFertilizerIds: this.getSelectedFertilizerIds(),
			Fertilizers: this.getSelectedFertilizers().map((fertilizer) => {
				return this._buildExistingFertilizerForm(fertilizer);
			})
		});
	}

	public updateFormState(form: FormGroup): void {
		if (!form) {
			return;
		}

		this.isTouched = form.pristine ? false : true;
		this.isFormValid = form.invalid ? false : true;
		this.form.emit(form);
	}

	private _buildCustomFertilizerForm(): IRanchCreateFertilizerForm {
		return {
			Name: this.fertilizer.Name,
			FormulationTypeId: this.fertilizer.FormulationTypeId,
			FormulationType: this._getFormulationType(this.fertilizer.FormulationTypeId),
			PoundsPerGallon: this.fertilizer.PoundsPerGallon,
			PercentageN: this._getNitrogenPercentage(),
			Nutrients: this.fertilizer.Nutrients,
			IsCustom: true
		};
	}

	private _buildExistingFertilizerForm(fertilizer: IFertilizer): IRanchCreateFertilizerForm {
		return {
			Id: fertilizer.Id,
			Name: fertilizer.Name,
			FormulationType: fertilizer.FormulationType,
			PoundsPerGallon: fertilizer.LbsPerGallon,
			PercentageN: fertilizer.PercentageN,
			IsCustom: false
		};
	}

	private _get(): void {
		let createModelPromise: Promise<RanchFertilizerViewModel>;
		let listPromise: Promise<IFertilizer[]>;

		createModelPromise = this.isRanchAgnostic ?
			this._service.getCreateModelRanchAgnostic() : this._service.getCreateModel();

		listPromise = this.isRanchAgnostic ?
			this._service.listSelectableRanchAgnostic() : this._service.listAvailable();

		createModelPromise.then(data => {
			if (!data) {
				this._notificationService.generateNotifcation({
					type: eNotificationTypes.ERROR,
					message: `Fertilizer information is empty. Please reload the page.`
				});

				return;
			} else if (!data.Nutrients || data.Nutrients.length === 0) {
				this._notificationService.generateNotifcation({
					type: eNotificationTypes.ERROR,
					message: `Fertilizer Nutrient information is incomplete. Please reload the page.`
				});
			}

			this.fertilizer = data;
			this.fertilizer.FormulationTypeId = eFertilizerFormulationTypes.DRY;
			this.fertilizer.FormulationType = this._getFormulationType(eFertilizerFormulationTypes.DRY);
		});

		listPromise.then((fertilizers: IFertilizer[]) => {
			this.fertilizers = fertilizers;
			this.selectableFertilizers = this._getSelectableFertilizers();
		});
	}

	private _getFormulationType(formulationTypeId: number): { Id: number, Name: string } {
		if (formulationTypeId === eFertilizerFormulationTypes.WET) {
			return { Id: eFertilizerFormulationTypes.WET, Name: 'Wet' };
		}

		return { Id: eFertilizerFormulationTypes.DRY, Name: 'Dry' };
	}

	private _getNitrogenPercentage(): number {
		let nitrogenNutrient = this.fertilizer.Nutrients
			.filter((nutrient) => nutrient.NutrientId === this._nitrogenNutrientId)[0];

		if (!nitrogenNutrient) {
			return 0;
		}

		return nitrogenNutrient.Percentage;
	}

	private _getSelectableFertilizers(): IFertilizer[] {
		if (!this.fertilizers) {
			return [];
		}

		return this.fertilizers.filter((fertilizer) =>
			this.selectedFertilizerIds.indexOf(fertilizer.Id) === -1 &&
			(this.isOrganicTab
				? fertilizer.IsChemical === false
				: fertilizer.IsChemical !== false));
	}
}
